/**
 * The tile map: terrain, the wall layer, and building occupancy.
 *
 * Walls live on the tile grid rather than in the building store because they
 * are placed as drag-lines, auto-tile against their neighbours, and need
 * per-tile damage that pathfinding can read cheaply every frame.
 */

import { MAP_H, MAP_W, MAP_TILES } from './constants.ts';
import { Terrain, TERRAIN_BUILDABLE, TERRAIN_MOVE_COST, WallKind } from './types.ts';
import { chance, nextFloat, nextInt, type Rng, shuffle } from '../core/rng.ts';
import { clamp, fbm2D } from '../core/math.ts';

/**
 * Ground that is not ground.
 *
 * One predicate, because the last pass proved that two of them drift. `Deep`
 * was **appended** to `Terrain` and every existing water test in this file
 * still read `=== Terrain.River`, so the seam scatter, the gate placer and the
 * Hearth clean-up all looked straight past the middle of the new river. The
 * seam scatter is the one that showed: it runs *after* `carveRiver`, so it
 * painted forest and rock over the deep channel, and `seedResourceNodes` —
 * which asks only "is this tile Forest?" — then grew trees and boulders in open
 * water. That is the "rocks and trees render in the water" the owner reported,
 * and it was never a rendering bug at all.
 *
 * `Bridge` is deliberately not water here. A bridge is the thing you are
 * allowed to stand on.
 */
function isWater(t: number): boolean {
  return t === Terrain.River || t === Terrain.Deep;
}

export interface SpawnGate {
  /** Tile position on the map edge. */
  x: number;
  y: number;
  /** Compass-ish label for the scouting panel: 0=N 1=NE 2=E 3=SE 4=S 5=SW 6=W 7=NW */
  dir: number;
  name: string;
}

export class TileMap {
  readonly w = MAP_W;
  readonly h = MAP_H;
  readonly terrain = new Uint8Array(MAP_TILES);
  readonly wall = new Uint8Array(MAP_TILES);
  readonly wallHp = new Float32Array(MAP_TILES);
  readonly wallMaxHp = new Float32Array(MAP_TILES);
  readonly wallTier = new Uint8Array(MAP_TILES);
  /** Index into BuildingStore, or -1. */
  readonly building = new Int16Array(MAP_TILES);
  /** Render-only per-tile variation. Part of world state so it survives save/load. */
  readonly decor = new Uint8Array(MAP_TILES);
  /** Persistent scorch/blood staining, 0..255. Render-only but simulated cheaply. */
  readonly stain = new Uint8Array(MAP_TILES);
  /**
   * Rubble left where a building was smashed. Blocks construction until a
   * villager clears it, and remembers what stood there so the player can put it
   * back with one click. 0 = clear, otherwise buildingTypeIndex + 1.
   */
  readonly rubble = new Uint16Array(MAP_TILES);
  /** Seconds of clearing work left on a rubble tile. */
  readonly rubbleWork = new Float32Array(MAP_TILES);

  gates: SpawnGate[] = [];
  hearthX = MAP_W >> 1;
  hearthY = MAP_H >> 1;

  /**
   * Bumped whenever a terrain tile changes (a road laid, ground scorched to
   * ash). The renderer bakes terrain in chunks and watches this to know when a
   * chunk needs re-baking; nothing in the simulation reads it.
   */
  terrainVersion = 0;

  /**
   * Bumped whenever the wall layer changes shape — a section raised, upgraded,
   * demolished or knocked down.
   *
   * Render-only, exactly like `terrainVersion`, and deliberately neither hashed
   * nor saved: it is a change *counter*, not a fact about the world, and two
   * worlds that reached the same wall by different routes are the same world.
   * `CurtainWatch` in `systems/curtain.ts` watches it so that "is my circuit
   * closed" — which is a flood fill over the whole enclosure — is recomputed
   * when a wall moves and not sixty times a second.
   */
  wallVersion = 0;

  /**
   * Derived index of which tiles carry rubble — never hashed, never saved,
   * rebuilt from `rubble` on demand.
   *
   * `nearestRubble` used to answer "where is the closest pile" by sweeping a
   * 45×45 box of tiles, once per idle villager per tick. That is two thousand
   * reads per villager, and a city has thousands of idle villagers the moment a
   * siege knocks a quarter of it down — it was, on the profile, the second most
   * expensive thing in the whole simulation. Sweeping a list of the few hundred
   * tiles that are actually rubble answers the same question.
   *
   * Ascending order is load-bearing: the box sweep visited tiles row-major and
   * kept the first strict minimum, so an ascending list breaks ties the same
   * way and the answer is unchanged, not merely similar.
   *
   * Correctness under mutation is handled two ways, because a derived cache
   * that can change what happens is not a cache. The index is rebuilt at most
   * once per tick, and every reader re-checks `rubble[k]` before trusting an
   * entry — so a pile cleared by one villager is invisible to the next within
   * the same tick, exactly as it was before.
   */
  private rubbleIdx: Int32Array | null = null;
  private rubbleIdxN = 0;
  private rubbleIdxStamp = -1;

  /** Force a rebuild — for anything that creates rubble mid-tick. */
  invalidateRubbleIndex(): void {
    this.rubbleIdxStamp = -1;
  }

  /** Tile indices carrying rubble as of this tick, ascending. */
  rubbleTiles(tick: number): { list: Int32Array; n: number } {
    if (this.rubbleIdx === null) this.rubbleIdx = new Int32Array(MAP_TILES);
    if (this.rubbleIdxStamp !== tick) {
      const list = this.rubbleIdx;
      let n = 0;
      for (let i = 0; i < MAP_TILES; i++) {
        if (this.rubble[i] !== 0) list[n++] = i;
      }
      this.rubbleIdxN = n;
      this.rubbleIdxStamp = tick;
    }
    return { list: this.rubbleIdx, n: this.rubbleIdxN };
  }

  idx(x: number, y: number): number {
    return y * this.w + x;
  }

  inBounds(x: number, y: number): boolean {
    return x >= 0 && y >= 0 && x < this.w && y < this.h;
  }

  reset(): void {
    this.terrain.fill(Terrain.AshPlain);
    this.wall.fill(WallKind.None);
    this.wallHp.fill(0);
    this.wallMaxHp.fill(0);
    this.wallTier.fill(0);
    this.building.fill(-1);
    this.decor.fill(0);
    this.stain.fill(0);
    this.rubble.fill(0);
    this.rubbleWork.fill(0);
    this.invalidateRubbleIndex();
    this.wallVersion++;
    this.gates = [];
  }

  /** Say that the curtain has changed shape. See `wallVersion`. */
  wallChanged(): void {
    this.wallVersion++;
  }

  /** True if ground units cannot walk here at all. */
  isImpassable(x: number, y: number): boolean {
    if (!this.inBounds(x, y)) return true;
    const i = this.idx(x, y);
    if (this.building[i] >= 0) return true;
    if (this.wall[i] !== WallKind.None) return true;
    return false;
  }

  moveCost(x: number, y: number): number {
    if (!this.inBounds(x, y)) return Infinity;
    const i = this.idx(x, y);
    if (this.building[i] >= 0) return Infinity;
    if (this.wall[i] !== WallKind.None) return Infinity;
    return TERRAIN_MOVE_COST[this.terrain[i]] ?? 1;
  }

  canBuild(x: number, y: number): boolean {
    if (!this.inBounds(x, y)) return false;
    const i = this.idx(x, y);
    if (this.building[i] >= 0) return false;
    if (this.wall[i] !== WallKind.None) return false;
    if (this.rubble[i] !== 0) return false;
    return TERRAIN_BUILDABLE[this.terrain[i]] ?? false;
  }
}

const GATE_NAMES = [
  'the Cairn Road',
  'the Weeping Ford',
  'Blackbriar Cut',
  'the Gallows Track',
  'the Ashen Steps',
  'Widow Pass',
  'the Drowned Mile',
  'Hollow Gate',
];

const DIR_NAMES = ['north', 'north-east', 'east', 'south-east', 'south', 'south-west', 'west', 'north-west'];

export function gateDirName(dir: number): string {
  return DIR_NAMES[dir % 8];
}

/**
 * Procedural map generation. Deterministic in `rng`.
 *
 * Shape of a good map: a buildable bowl in the middle for the settlement, a
 * scatter of resource terrain at mid-radius so the player has to expand outward
 * into danger, and roads that telegraph where the enemy will come from.
 */
/**
 * How a country bends the settlement's own terrain mix.
 *
 * Each field shifts the *threshold* a tile has to clear to become that terrain,
 * so a negative number means "more of this". They are offsets rather than
 * absolute values on purpose: the baseline below was tuned against measured
 * node distances — see the long note on `centreClear` — and a table of absolute
 * thresholds would quietly discard that work the first time somebody added a
 * country.
 *
 * `wet` is the marsh threshold, `stone` the outcrop, `wood` the forest, `ore`
 * the iron seam. `bare` scatters extra ruin-and-rubble ground for places where
 * the story is that nothing grows.
 *
 * The unknown-country fallback is the temperate baseline, which is what every
 * settlement in the game had before this existed — so a `biome` this table has
 * never heard of degrades to exactly the old behaviour rather than to a blank
 * map.
 */
const BIOME_MIX: Record<string, { wood: number; stone: number; wet: number; ore: number; bare: number }> = {
  // The baseline the whole generator was tuned against: wood to work, stone to
  // walk to. Every row below is a departure from this one, and the test of the
  // table is whether the rows read differently across, not whether each is
  // plausible on its own.
  plain: { wood: 0.02, stone: 0, wet: 0, ore: 0, bare: 0 },
  // Timber everywhere and little bare rock: a hold in the deep wood is short of
  // stone, not of wood, and that is a genuinely different opening.
  forest: { wood: -0.14, stone: 0.05, wet: -0.02, ore: 0.01, bare: 0 },
  jungle: { wood: -0.18, stone: 0.07, wet: -0.08, ore: 0.012, bare: 0 },
  // The mirror of it. Stone and ore are underfoot; the woodcutters have to go a
  // long way, which is what makes `deadfall.ts`'s hatch matter here. This is the
  // one country the owner asked to be *richer* in rock, not poorer.
  mountain: { wood: 0.2, stone: -0.21, wet: 0.14, ore: -0.10, bare: 0.008 },
  hill: { wood: 0.06, stone: -0.13, wet: 0.06, ore: -0.05, bare: 0 },
  /**
   * Sand, and almost nothing standing on it.
   *
   * The owner's note is that a desert should be *rarer* in wood than it was and
   * that the palms are a later art job. So this is open ground first: the wood
   * cut is the hardest in the table and the stone cut goes the *other* way from
   * the old one, which had a desert stonier than a plain — a desert is not a
   * quarry, it is a place with nothing in it. What the renderer then does with
   * all that open ground is the country's own fabric, which is already sand.
   */
  desert: { wood: 0.42, stone: 0.06, wet: 0.35, ore: 0.02, bare: 0 },
  // `bare: 0` on both the desert and the ice is deliberate and was measured
  // wrong first: broken ground is `Terrain.Ruin`, which draws as somebody's
  // fallen walls. A desert of rubble is a ruin with the heating on. What makes
  // sand read as sand is *open* ground — `AshPlain` wearing the country's own
  // fabric, which `render/biome.ts` already paints as dune and as snow — so the
  // way to build a desert here is to take almost everything else away.
  // Frozen: no timber to speak of, and rock showing through the snow rather
  // than a mountainside of it. No standing water, because it is not liquid.
  ice: { wood: 0.36, stone: 0.005, wet: 0.3, ore: 0.015, bare: 0 },
  // Water everywhere and poor ground under it.
  marsh: { wood: -0.04, stone: 0.1, wet: -0.24, ore: 0.02, bare: 0 },
  // A shore: open, a little damp, no great stone.
  coast: { wood: 0.05, stone: 0.04, wet: -0.06, ore: 0.015, bare: 0 },
  // Somebody was here before, and the ground is full of their walls. The one
  // country whose signature is the broken ground itself.
  ruin: { wood: 0.05, stone: -0.05, wet: 0.02, ore: -0.02, bare: 0.04 },
};

export function generateMap(map: TileMap, rng: Rng, biome?: string): void {
  map.reset();
  const seedA = nextInt(rng, 0, 1 << 30);
  const seedB = nextInt(rng, 0, 1 << 30);
  const seedC = nextInt(rng, 0, 1 << 30);

  /**
   * The country this hold stands in, or the temperate baseline.
   *
   * Read once rather than per tile: this is a hundred and forty-seven thousand
   * tiles and a `Record` lookup inside that loop is the sort of thing that
   * turns a generator into a stall.
   */
  const mix = BIOME_MIX[biome ?? 'plain'] ?? BIOME_MIX.plain;

  const cx = map.w / 2;
  const cy = map.h / 2;
  /** Everything below is authored against a 64-tile map and scaled from there. */
  const S = map.w / 64;

  // Base terrain from layered noise, biased so the centre is clean ash plain.
  for (let y = 0; y < map.h; y++) {
    for (let x = 0; x < map.w; x++) {
      const i = map.idx(x, y);
      const nx = x / (12 * S);
      const ny = y / (12 * S);
      const dCentre = Math.hypot(x - cx, y - cy) / (map.w / 2);

      const forest = fbm2D(nx, ny, seedA, 4);
      const rock = fbm2D(nx + 31.7, ny - 12.3, seedB, 3);
      const rough = fbm2D(nx * 2.1 - 5, ny * 2.1 + 9, seedC, 2);

      let t: number = Terrain.AshPlain;
      /**
       * A clear build bowl in the middle — but a much smaller one than it used
       * to be, and the reason is that the argument for the old size inverted.
       *
       * This read `(dCentre - 0.2) / 0.16`, and its comment said the settlement
       * "needs room to sprawl before it has to start clearing forest for
       * space". That was right while forest was an **obstacle**: something in
       * the way of a foundation, to be cleared. Under the resource-node model
       * forest is the **resource**, and a bowl sized to keep it at arm's length
       * is a bowl that guarantees a town with nothing to harvest.
       *
       * It was not a near miss. Measured across six maps, the nearest node of
       * any kind was **33 to 36 tiles** from the hearth and there were *zero*
       * within twenty — against a `HARVEST_RADIUS` of 12. Every woodcutter,
       * quarry and mine a player could actually place fell through to the
       * dry-camp fallback, for ever, on every map. The trees existed and no
       * town could reach one.
       *
       * The new ramp opens the ground at about **21 tiles** for forest, 22 for
       * stone and 24 for iron. Re-measured on the same six maps: nearest node
       * **16 to 18** tiles, and 7 to 52 of them inside twenty. That still
       * leaves a clear core wider than any town in the shots — they span about
       * 28 tiles corner to corner — so nothing has lost its room to sprawl.
       * What it buys is that where you put a camp is now a decision: one at the
       * edge of the bowl reaches wood, one tucked in beside the Hearth does not.
       */
      const centreClear = clamp((dCentre - 0.085) / 0.07, 0, 1);

      /**
       * **Stone was a fifth of a temperate plain**, and that is the whole of
       * the owner's *"there is still way to many iron and stone nodes"*.
       *
       * Measured with `npm run climate` before this pass: a `plain` came out
       * 20.8% stone outcrop and 3.8% iron seam against 18.6% forest — so bare
       * rock was as common as the wood, on the one country that is supposed to
       * be the gentle opening. A quarry is meant to be a place you walk to.
       *
       * The base cut moves 0.63 → 0.71 for rock and the seam's 0.24 → 0.195,
       * and the per-country offsets below are re-derived against the new base
       * rather than carried over — an offset table is only meaningful relative
       * to the number it offsets, and leaving them would have made every
       * country stonier than the plain it was measured against.
       */
      if (forest > 0.58 + mix.wood && centreClear > 0.35) t = Terrain.Forest;
      if (rock > 0.80 + mix.stone && centreClear > 0.45) t = Terrain.StoneOutcrop;
      if (rough > 0.72 + mix.wet && dCentre > 0.5) t = Terrain.Marsh;
      if (rough < 0.15 - mix.ore && centreClear > 0.6 && rock > 0.62 + mix.stone) t = Terrain.IronSeam;
      /**
       * Broken ground, and why it used to do nothing at all.
       *
       * The gate was `forest < 0.3 && rock < 0.32` — two fbm fields both below
       * a third of their range at the same tile. fbm clusters hard around its
       * middle, so almost nothing qualified: `ruin` country measured **0.1%**
       * broken ground, and the `bare` term that was supposed to be the whole
       * character of a place where somebody built before was invisible.
       *
       * The gate is now the ordinary "this is open ground" test the other
       * terrains use, and `bare` is a real threshold on `rough` rather than a
       * dice roll behind an impossible door. A country with `bare` at zero
       * still gets none, so the temperate baseline is unchanged.
       */
      if (mix.bare > 0 && t === Terrain.AshPlain && centreClear > 0.2 && rough > 0.78 - mix.bare * 4) {
        t = Terrain.Ruin;
      }

      map.terrain[i] = t;
      map.decor[i] = nextInt(rng, 0, 255);
    }
  }

  seedHomeSeams(map, rng);
  carveRiver(map, rng);
  scatterSeams(map, rng);
  // After the seams, so it catches what the scatter dropped as well as what the
  // carve left behind; before the roads, so a road still meets a whole river and
  // bridges it in one span rather than hopping an island in the middle.
  drownIslands(map);
  placeGates(map, rng);
  carveRoads(map, rng);
  /**
   * And again, because the roads changed the water.
   *
   * The first pass is deliberately before the roads — a road should meet a
   * whole river and bridge it in one span rather than hopping an island in the
   * middle — and that is still why it is there. But a crossing *leaves the
   * water it does not deck*, and since a bridge became a ruled line rather than
   * the road's own wandering course, the tiles the wander used to pave are
   * water again. A seam tile beside them can be enclosed by water that was not
   * there when the first pass looked.
   *
   * This was found by `tests/maprules.test.ts` the day the bridges were
   * straightened — three trees standing in a river on seed 7 — and it is worth
   * being precise about the blame: those trees were **always marooned**. The
   * old crooked deck happened to pave over enough of the channel beside them to
   * take them under the five-of-eight threshold, so the invariant was being met
   * by accident. Straightening the bridge did not break it; it stopped hiding
   * that it was broken.
   */
  drownIslands(map);

  // The Hearth footprint must always be clean.
  for (let dy = -4; dy <= 4; dy++) {
    for (let dx = -4; dx <= 4; dx++) {
      const x = map.hearthX + dx;
      const y = map.hearthY + dy;
      if (!map.inBounds(x, y)) continue;
      const i = map.idx(x, y);
      if (isWater(map.terrain[i])) map.terrain[i] = Terrain.AshPlain;
      if (Math.abs(dx) <= 1 && Math.abs(dy) <= 1) map.terrain[i] = Terrain.Ruin;
    }
  }
}

/**
 * The water: one big river, and a couple of small ones.
 *
 * ── What changed, and why the old one was invisible ─────────────────────────
 *
 * There has always been a river. It was one to two tiles wide, and it was
 * pushed away from the middle by `9 * (map.w / 64)` — **fifty-four tiles** on
 * the shipped 384-wide map. So it never came within sight of a town, never
 * shaped an approach, and was a strip of blue somebody found once by panning.
 *
 * A river is supposed to be a *decision*: something an army has to cross, at a
 * cost, in front of you. So the main channel is now wide, it is `Terrain.Deep`
 * down the middle with fordable `River` at its edges, and it is allowed within
 * `RIVER_KEEP_OUT` of the Hearth rather than half the map. It still never runs
 * over the Hearth's own footprint — a settlement that opens under water is not
 * a hard start, it is a broken one.
 *
 * ── Deep, shallow, and why both ────────────────────────────────────────────
 *
 * All-deep would make the river a wall with two gates, and a wall is what the
 * curtain is for. Banks of shallow give a wide crossing that is *possible* and
 * slow — you can wade it and arrive strung out and late — while the middle is
 * bad enough that a bridge is worth building. That gradient is the whole
 * mechanic; see `TERRAIN_MOVE_COST` for the numbers and the argument against
 * making `Deep` impassable outright.
 */

/** The main channel never comes closer than this to the Hearth, in tiles. */
const RIVER_KEEP_OUT = 15;

function carveRiver(map: TileMap, rng: Rng): void {
  /**
   * The main channel is aimed past the town rather than dropped anywhere.
   *
   * Left to a free start it crossed wherever it liked, and measured over four
   * seeds it came within 18 tiles of one Hearth and 122 of another — and a
   * river 122 tiles away is a river no siege will ever be fought across, which
   * is the whole reason the brief asked for bigger ones. So it starts within a
   * band either side of the Hearth's own cross-axis: far enough out that the
   * keep-out circle only ever clips it, close enough that an army coming from
   * that quarter has to wade.
   *
   * Which side is a coin, so a town is not always on the same bank.
   */
  const vertical = chance(rng, 0.5);
  const seat = vertical ? map.hearthX : map.hearthY;
  const side = chance(rng, 0.5) ? 1 : -1;
  carveChannel(map, rng, {
    vertical,
    start: seat + side * (RIVER_KEEP_OUT + 4 + nextInt(rng, 0, 22)),
    halfWidth: 3 + nextInt(rng, 0, 3),
    deep: true,
    keepOut: RIVER_KEEP_OUT,
    wander: 3.4,
  });

  /**
   * Two lesser streams, always shallow and allowed much closer in.
   *
   * The brief asked for small ones as well as big, and they earn their place
   * mechanically rather than decoratively: a one-tile ford beside the town is
   * ground a defender can stand behind cheaply, without the map committing to
   * a second bridge.
   */
  const brooks = 1 + nextInt(rng, 0, 2);
  for (let n = 0; n < brooks; n++) {
    carveChannel(map, rng, {
      vertical: chance(rng, 0.5),
      start: -1,
      halfWidth: nextInt(rng, 0, 2),
      deep: false,
      keepOut: 7,
      wander: 2.6,
    });
  }
}

interface Channel {
  vertical: boolean;
  /** Where it crosses the far edge. −1 lets it start anywhere sensible. */
  start: number;
  /** Tiles either side of the centre line. 0 gives a one-tile brook. */
  halfWidth: number;
  /** Whether the middle of it is `Deep`. A brook never is. */
  deep: boolean;
  keepOut: number;
  wander: number;
}

function carveChannel(map: TileMap, rng: Rng, c: Channel): void {
  const seed = nextInt(rng, 0, 1 << 30);
  const span = c.vertical ? map.h : map.w;
  const cross = c.vertical ? map.w : map.h;
  let pos =
    c.start >= 0
      ? clamp(c.start, 3, cross - 4)
      : nextInt(rng, Math.floor(cross * 0.2), Math.floor(cross * 0.8));

  for (let s = 0; s < span; s++) {
    /**
     * The meander, with a leash.
     *
     * Noise alone is a random walk, and a random walk over three hundred and
     * eighty steps migrates: aiming the channel at the town got its *start*
     * right and it still ended up seventy-nine tiles away by the time it drew
     * level. So it is pulled gently back toward the line it was aimed at — the
     * drift is what makes it a river rather than a canal, and the leash is what
     * keeps it the river of *this* town.
     *
     * No centre-avoiding term any more. What keeps water off the Hearth is the
     * footprint test below, which is a circle rather than a hemisphere, so a
     * town may sit on either bank.
     */
    const aim = c.start >= 0 ? c.start : pos;
    pos = clamp(
      pos + (fbm2D(s / 11, 0.5, seed, 3) - 0.5) * c.wander + (aim - pos) * 0.02,
      2,
      cross - 3,
    );

    // The channel breathes along its length, so it is not a canal.
    const swell = fbm2D(s / 17, 3.3, seed + 11, 2);
    const half = Math.max(0, Math.round(c.halfWidth * (0.55 + swell * 0.9)));

    for (let off = -half; off <= half; off++) {
      const p = Math.round(pos) + off;
      const x = c.vertical ? p : s;
      const y = c.vertical ? s : p;
      if (!map.inBounds(x, y)) continue;
      if (Math.hypot(x - map.hearthX, y - map.hearthY) < c.keepOut) continue;
      const i = map.idx(x, y);
      // Never eat a leyline: three patches a map, and one of them can be the
      // only route to the second Age. Same rule the roads keep.
      if (map.terrain[i] === Terrain.Leyline) continue;
      // Deep down the middle, fordable at the edges. `half` of 0 or 1 is a
      // brook and is shallow all the way across whatever the caller asked.
      const core = c.deep && half >= 2 && Math.abs(off) <= half - 2;
      map.terrain[i] = core ? Terrain.Deep : Terrain.River;
    }

    // Marshy banks, outside the water itself.
    for (const side of [-1, 1]) {
      for (let k = 1; k <= 2; k++) {
        const p = Math.round(pos) + side * (half + k);
        const bx = c.vertical ? p : s;
        const by = c.vertical ? s : p;
        if (!map.inBounds(bx, by) || !chance(rng, 0.3)) continue;
        const bi = map.idx(bx, by);
        if (map.terrain[bi] === Terrain.AshPlain) map.terrain[bi] = Terrain.Marsh;
      }
    }
  }
}

/**
 * Drown anything left standing in the middle of the water.
 *
 * The seam scatter was only half of "rocks and trees render in the water", and
 * the measurement is what said so: stopping the scatter from painting over the
 * channel gave back 37–268 deep tiles a map, and *still* left four to eighteen
 * tiles of forest and rock marooned mid-river on every seed.
 *
 * The rest comes from the carve itself, which paints a cross-section per step
 * along the channel and therefore leaves holes wherever a step skips ground:
 * the `keepOut` circle that holds water off the Hearth, the leyline it refuses
 * to eat, and — the common one — the diagonal, where the centre line moves
 * sideways faster than one tile per step and the cross-sections stop
 * overlapping. Every one of those keeps whatever terrain was there before, so a
 * stand of trees ends up with the river on all sides of it.
 *
 * Rather than chase three causes and the fourth one nobody has found yet, this
 * states the rule the picture actually needs: **a tile with water most of the
 * way round it is water.** Two passes, because drowning a tile can strand its
 * neighbour; collected before it is applied, so the result cannot depend on
 * scan order; no `Rng`, so the stream is untouched and every other roll on the
 * map lands where it did.
 *
 * The Hearth's own keep-out is exempt, and that is the point of the exemption:
 * the hole the channel leaves around the town is *deliberate*, and a settlement
 * that opens under water is not a hard start, it is a broken one.
 */
function drownIslands(map: TileMap): void {
  /**
   * One sweep, then a work-list — and the work-list is a performance fix with a
   * measurement behind it, not a tidy-up.
   *
   * The rule is iterative by nature: drowning a tile can strand its neighbour.
   * The first cut of the fixpoint version simply re-swept all 147,456 tiles
   * until a pass changed nothing, which is correct and cost **358 ms a map
   * against 42 ms before it** — 8.4x, measured. Nothing in the game generates
   * maps in a loop except the tests, and that is exactly where it showed up:
   * `realmevents` walks every branch of every card, builds a realm for each,
   * and went from comfortable to over its eight-minute budget.
   *
   * Only a tile *next to something that just drowned* can newly become
   * enclosed, so after the opening sweep the frontier is the drowned tiles'
   * neighbours and nothing else. Same fixpoint, same guarantee — zero seam
   * tiles left with water on five of eight sides — for the cost of the water's
   * own edge instead of the whole map, every pass.
   */
  const W = map.w;
  const H = map.h;
  /**
   * Every tile is settled at most once, and this is not an optimisation.
   *
   * Without it the near-Hearth case never terminates. A marooned tile inside
   * `RIVER_KEEP_OUT` becomes `AshPlain`, which is **not water** — so it still
   * has water on five of its eight sides, still reads as enclosed, and is
   * pushed again every time a neighbour is examined. The queue grows until the
   * engine refuses it: `RangeError: Invalid array length`, thrown out of
   * `queue.push`, which is what `tests/scale.test.ts` caught.
   *
   * The re-sweep version this replaces had the same flaw and hid it: it simply
   * kept finding the tile every pass and stopped when it hit its pass cap, so
   * the non-termination showed up as *cost* rather than as a crash. That is the
   * more dangerous of the two failures, and it is the one that was shipped.
   */
  const settled = new Uint8Array(W * H);

  /** What this tile should become if it is enclosed, or -1 to leave it alone. */
  const verdict = (x: number, y: number): number => {
    if (x <= 0 || y <= 0 || x >= W - 1 || y >= H - 1) return -1;
    const i = map.idx(x, y);
    if (settled[i] === 1) return -1;
    const t = map.terrain[i];
    // Roads and their decks are never drowned. A bridge tile with water on
    // every side is not a marooned island, it is a bridge — and drowning a road
    // because the river runs alongside it would cut the track in two.
    if (isWater(t) || t === Terrain.Leyline || t === Terrain.Road || t === Terrain.Bridge) return -1;
    let n = 0;
    let deep = 0;
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        if (dx === 0 && dy === 0) continue;
        const nt = map.terrain[map.idx(x + dx, y + dy)];
        if (isWater(nt)) n++;
        if (nt === Terrain.Deep) deep++;
      }
    }
    // Five of eight: enclosed enough that it reads as being *in* the river
    // rather than on a bank of it.
    if (n < 5) return -1;
    /**
     * Near the Hearth, a marooned tree is *cleared* rather than drowned.
     *
     * `RIVER_KEEP_OUT` exists so the channel never creeps up on the town, and
     * honouring it by simply skipping the tile left the one thing this pass
     * exists to prevent — a stand of trees with water on every side — standing
     * where the player can see it best. Both answers satisfy "nothing grows in
     * the water"; only this one also keeps the keep-out, because open ground is
     * not river.
     */
    if (Math.hypot(x - map.hearthX, y - map.hearthY) < RIVER_KEEP_OUT) return Terrain.AshPlain;
    return deep >= 4 ? Terrain.Deep : Terrain.River;
  };

  const queue: number[] = [];
  for (let y = 1; y < H - 1; y++) {
    for (let x = 1; x < W - 1; x++) {
      const to = verdict(x, y);
      if (to < 0) continue;
      const i = map.idx(x, y);
      map.terrain[i] = to;
      settled[i] = 1;
      queue.push(i);
    }
  }

  /**
   * The opening sweep applies as it goes rather than in a batch, which is a
   * deliberate difference from the version this replaces.
   *
   * Batching meant a tile's neighbour count was read against the state at the
   * *start* of the pass, so the whole thing had to be repeated to see the
   * consequences. Applying immediately means a run of enclosed tiles collapses
   * in the same sweep, and the work-list below only has to carry what the sweep
   * could not reach going forwards.
   */
  for (let head = 0; head < queue.length; head++) {
    const i = queue[head];
    const x = i % W;
    const y = (i / W) | 0;
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        if (dx === 0 && dy === 0) continue;
        const ax = x + dx;
        const ay = y + dy;
        const to = verdict(ax, ay);
        if (to < 0) continue;
        const k = map.idx(ax, ay);
        map.terrain[k] = to;
        settled[k] = 1;
        queue.push(k);
      }
    }
  }
}

/**
 * One outcrop and one seam within reach of the Hearth, on every map.
 *
 * > *"there is still way to much stone and iron and stuff not in the mountains"*
 *
 * Thinning the stone until it is genuinely a mountain resource is the ask, and
 * it walks straight into a failure this file has already recorded once. The
 * `centreClear` note above describes it exactly: when the nearest node a player
 * can reach is further out than `HARVEST_RADIUS`, "every woodcutter, quarry and
 * mine a player could actually place fell through to the dry-camp fallback, for
 * ever, on every map".
 *
 * Measured with `npm run climate`, thinning alone did that to stone: the
 * nearest rock on a temperate plain went from 27.5 tiles to **47.6**, with zero
 * inside twenty on every one of the ten countries. The town cannot walk that
 * far, so a quarry becomes a building that does nothing.
 *
 * The answer is not to put the stone back — it is to stop confusing *how much
 * of a thing a country has* with *whether the game can be started*. Those are
 * different questions and the old table answered them with one number. So every
 * hold gets a home outcrop and a home seam, modest and deliberate, and
 * everything beyond them is the country's own business. A mountain is still
 * four times a plain's rock; a plain simply has one quarry's worth of it by the
 * door instead of none at all.
 *
 * Placed off the world's own seeded stream, so two worlds with the same seed
 * still grow the same ground. Water, roads and the leyline are never overwritten
 * — this runs before the river is carved, so a blob that lands on the channel is
 * cut through by it a moment later, which is correct: the river wins, and the
 * `drownIslands` pass tidies whatever it strands.
 */
function seedHomeSeams(map: TileMap, rng: Rng): void {
  /**
   * Far enough to be *outside the build bowl* and inside a day's walk.
   *
   * The bowl runs to about sixteen tiles and the shots show a town spanning
   * roughly twenty-eight corner to corner, so a patch at eighteen to
   * twenty-four is on the edge of the settlement rather than under it: close
   * enough that a camp placed at the rim reaches it, far enough that it is not
   * standing in the middle of the market square.
   */
  const blob = (kind: number, radius: number, at: number): void => {
    const dist = 18 + nextFloat(rng) * 6;
    const x0 = Math.round(map.hearthX + Math.cos(at) * dist);
    const y0 = Math.round(map.hearthY + Math.sin(at) * dist);
    for (let dy = -radius - 1; dy <= radius + 1; dy++) {
      for (let dx = -radius - 1; dx <= radius + 1; dx++) {
        const x = x0 + dx;
        const y = y0 + dy;
        if (!map.inBounds(x, y)) continue;
        // A ragged edge, not a disc: a stamped circle of rock reads as a stamp.
        const r = Math.hypot(dx, dy) + (nextFloat(rng) - 0.5) * 1.6;
        if (r > radius) continue;
        const i = map.idx(x, y);
        const t = map.terrain[i];
        if (isWater(t) || t === Terrain.Leyline || t === Terrain.Road || t === Terrain.Bridge) continue;
        map.terrain[i] = kind;
      }
    }
  };

  // Three quarters of the compass apart, so they are not all out of one gate —
  // and so a horde arriving on one vector cannot sit on the whole economy.
  const first = nextFloat(rng) * Math.PI * 2;
  blob(Terrain.StoneOutcrop, 7, first);
  blob(Terrain.IronSeam, 6, first + (2 * Math.PI) / 3);
  blob(Terrain.StoneOutcrop, 6, first + (4 * Math.PI) / 3);
  /**
   * A second seam, and it is here because a *test* said so rather than because
   * the picture wanted it.
   *
   * With one iron blob at radius 4 the ore lattice (9) put barely a single node
   * inside the town's reach, and `tests/bindings.test.ts` caught what that
   * means: the Vigil raised **nothing in five minutes**, because that binding
   * wants iron before it can put anybody in the field. One node is not a mine,
   * it is a rock with iron in it.
   *
   * The blobs are sized against the lattice they will be sampled at, which is
   * the thing easy to forget: a patch smaller than a lattice block is a patch
   * that may contain no node at all.
   */
  blob(Terrain.IronSeam, 5, first + Math.PI / 3);
}

function scatterSeams(map: TileMap, rng: Rng): void {
  const S = map.w / 64;
  const wants: Array<{ t: number; blobs: number; size: number; minR: number; maxR: number }> = [
    { t: Terrain.Forest, blobs: Math.round(9 * S), size: Math.round(30 * S), minR: 11 * S, maxR: 28 * S },
    { t: Terrain.StoneOutcrop, blobs: Math.round(6 * S), size: Math.round(18 * S), minR: 12 * S, maxR: 29 * S },
    { t: Terrain.IronSeam, blobs: Math.round(5 * S), size: Math.round(12 * S), minR: 14 * S, maxR: 30 * S },
    /**
     * Leylines: the rarest ground on the map.
     *
     * Three small patches against nine forests, because a Wellspring is the
     * root of the whole second tier and the interesting question about the
     * Kindling should be *whether you can hold the ground it needs*, not
     * whether you can afford the building.
     *
     * Their band deliberately overlaps the forests' rather than the iron's: a
     * leyline in the treeline is an Arcane Sawmill sitting on its own supply,
     * and the district Chain bonus already pays for noticing that. Outside the
     * safe bowl like everything else, but not so far out that the second Age is
     * a march.
     *
     * Seeded last so they overlay a forest or a seam rather than being
     * overlaid — a leyline painted over by the iron pass would leave a map with
     * no path to Age 2 at all, which is the one generation outcome that cannot
     * be allowed.
     */
    { t: Terrain.Leyline, blobs: 3, size: Math.round(5 * S), minR: 12 * S, maxR: 28 * S },
  ];
  const cx = map.w / 2;
  const cy = map.h / 2;

  for (const want of wants) {
    for (let b = 0; b < want.blobs; b++) {
      const ang = nextFloat(rng) * Math.PI * 2;
      const rad = want.minR + nextFloat(rng) * (want.maxR - want.minR);
      const sx = Math.round(cx + Math.cos(ang) * rad);
      const sy = Math.round(cy + Math.sin(ang) * rad);
      if (!map.inBounds(sx, sy)) continue;
      // Random-walk blob fill.
      let px = sx;
      let py = sy;
      for (let s = 0; s < want.size; s++) {
        if (map.inBounds(px, py)) {
          const i = map.idx(px, py);
          // `isWater`, not `!== River`. See the note at the predicate: this one
          // line is what put trees in the middle of the channel.
          if (!isWater(map.terrain[i])) map.terrain[i] = want.t;
        }
        px += nextInt(rng, -1, 1);
        py += nextInt(rng, -1, 1);
        px = clamp(px, 1, map.w - 2);
        py = clamp(py, 1, map.h - 2);
      }
    }
  }
}

/** Eight candidate invasion gates around the rim; the director picks from these. */
function placeGates(map: TileMap, rng: Rng): void {
  const names = shuffle(rng, GATE_NAMES.slice());
  const cx = map.w / 2;
  const cy = map.h / 2;
  const r = Math.min(map.w, map.h) / 2 - 1.5;
  for (let d = 0; d < 8; d++) {
    const ang = (d / 8) * Math.PI * 2 - Math.PI / 2;
    let x = Math.round(cx + Math.cos(ang) * r);
    let y = Math.round(cy + Math.sin(ang) * r);
    x = clamp(x, 1, map.w - 2);
    y = clamp(y, 1, map.h - 2);
    // Gates must be standable — and a horde mustering in the deep end of a
    // river is exactly as broken as one mustering in a ford, so `isWater`.
    const i = map.idx(x, y);
    if (isWater(map.terrain[i])) map.terrain[i] = Terrain.AshPlain;
    map.gates.push({ x, y, dir: d, name: names[d] });
  }
}

/**
 * Roads from every gate to the Hearth. They telegraph the approach vectors.
 *
 * ── Why they used to look like a compass rose ──────────────────────────────
 *
 * The old walk stepped one tile at a time toward the Hearth, alternating axes
 * whenever both differed. That is the straightest line a grid can draw, and
 * with eight gates evenly spaced round the edge it produced exactly what it
 * sounds like: an asterisk. Four perfectly straight diagonals and two
 * perfectly straight axes, meeting at a point, on every map ever generated.
 * The comment claimed the alternation made a road "look hand-cut". It does
 * not; it makes a 45° line out of stairs, which is still a 45° line.
 *
 * ── What it does now ───────────────────────────────────────────────────────
 *
 * The road is still a road from a gate to the Hearth — that promise is what
 * the scouting panel and the director's approach vectors rest on — but it gets
 * there by wandering. The straight line is treated as a parameter `t` from 0 to
 * 1 and the path is pushed sideways by noise, with the push tapering to nothing
 * at both ends so it still *starts* at the gate and *ends* at the Hearth. The
 * result bends around things and arrives from roughly the right quarter, which
 * is what a track worn by feet does.
 *
 * The noise is seeded off the gate's own position, so a map's roads are as
 * deterministic as everything else here and two runs of the same seed lay the
 * same track.
 */
/**
 * The widest run of water a road will throw a bridge over, in tiles.
 *
 * A cap and not a promise. It exists so that a track running *alongside* the
 * channel for forty tiles does not pave the river into a causeway — that is a
 * road on the water, not a bridge over it. The main channel is authored a good
 * deal narrower than this where a road meets it square, so a genuine crossing
 * is always taken and a long graze along the bank never is.
 */
const MAX_BRIDGE_SPAN = 30;

/**
 * Lay one track, gate to gate, and bridge the water it meets.
 *
 * The walk is the wander described above, collected into a tile list rather
 * than paved as it goes — because a bridge is a property of a *run* of tiles
 * and cannot be decided one tile at a time. With the whole line in hand, each
 * maximal run of water is either short enough to be a crossing (paved
 * `Bridge`) or too long to be one (left as water, and the road simply stops at
 * the bank).
 */
function carveTrack(map: TileMap, rng: Rng, ax: number, ay: number, bx: number, by: number): void {
  const seed = (ax * 73856093) ^ (ay * 19349663);
  const dx = bx - ax;
  const dy = by - ay;
  const len = Math.max(1, Math.hypot(dx, dy));
  const nx = -dy / len;
  const ny = dx / len;
  /**
   * How far the track may stray, in tiles.
   *
   * Widened from `min(20, len * 0.11)`. That was tuned for a gate-to-Hearth
   * spoke of about 190 tiles; a gate-to-gate run is twice that, and the same
   * amplitude over twice the length is half the angle — which is how the first
   * through-road came out looking like a ruled line.
   */
  const amp = Math.min(34, len * 0.16) * (0.6 + nextFloat(rng) * 0.8);
  const steps = Math.ceil(len * 1.5);

  const line: Array<[number, number]> = [];
  let px = ax;
  let py = ay;
  for (let i = 1; i <= steps; i++) {
    const t = i / steps;
    const bell = Math.sin(t * Math.PI);
    const drift = (fbm2D(t * 7, 0.5, seed, 3) - 0.5) * 2 * amp * bell;
    const qx = Math.round(ax + dx * t + nx * drift);
    const qy = Math.round(ay + dy * t + ny * drift);
    for (const step of walkLine(px, py, qx, qy)) line.push(step);
    px = qx;
    py = qy;
  }
  for (const step of walkLine(px, py, bx, by)) line.push(step);

  for (let i = 0; i < line.length; i++) {
    const [x, y] = line[i];
    if (!map.inBounds(x, y)) continue;
    if (!isWater(map.terrain[map.idx(x, y)])) {
      pave(map, x, y);
      continue;
    }
    // A run of water. Measure it, then decide once for the whole span.
    let j = i;
    while (j < line.length) {
      const [wx, wy] = line[j];
      if (!map.inBounds(wx, wy) || !isWater(map.terrain[map.idx(wx, wy)])) break;
      j++;
    }
    /**
     * **The bridge is straight, and the road is what bends to meet it.**
     *
     * > *"when you auto build bridges for roads, they bridges should be straight
     * > always"*
     *
     * This used to pave `Bridge` along `line` itself — the road's own wandering
     * course — so a track that was meandering when it happened to reach the
     * water carried that meander out over the span and back. A road may wander;
     * a bridge is a built thing and nobody builds a curved one out of timber
     * because the path to it curved.
     *
     * So the span is re-cut: bank to bank, in one Bresenham line, from the last
     * dry tile before the water to the first dry tile after it. The wandering
     * tiles in between are simply not paved — `i = j - 1` skips them either way
     * — and the deck is laid on the straight line instead.
     *
     * The *cap* is measured on the straight crossing rather than on the run of
     * wandering tiles, and that is a real difference rather than a tidy-up: a
     * track grazing along a bank can wander through thirty water tiles while
     * the water is four tiles wide. Measuring the thing actually being built is
     * what `MAX_BRIDGE_SPAN` was always trying to ask about.
     */
    /**
     * **The deck is axis-aligned, and that is the difference between straight
     * and *looking* straight.**
     *
     * The first cut of this re-cut the crossing as a ruled line from bank to
     * bank, and measured it: `npm run bridgeline` reported a mean bend of 0.445
     * tiles and a worst of 0.71, which is Bresenham rounding and nothing else.
     * The decks were straight. They were reported as crooked anyway, and the
     * reason is that a straight line is not a straight *picture* on a tile grid:
     * a deck crossing at forty-five degrees is a staircase, and a staircase of
     * planks is exactly what "the bridges still aren't straight" describes.
     *
     * So the crossing no longer follows the road's angle at all. From the last
     * dry tile it tries all four axes and takes the one with the **shortest run
     * of water**, which is both square to the picture and the nearest thing to
     * square across the channel — a bridge is built where the river is
     * narrowest, which is also what a person would do.
     *
     * The road then bends to meet it at both ends. That is the right way round:
     * a road is a path and may turn, a bridge is a structure and may not.
     */
    const from = i > 0 ? line[i - 1] : line[i];
    const AXES: Array<[number, number]> = [[1, 0], [-1, 0], [0, 1], [0, -1]];
    let deck: Array<[number, number]> = [];
    let bestSpan = Infinity;
    for (const [ax, ay] of AXES) {
      const run: Array<[number, number]> = [];
      let cx = from[0] + ax;
      let cy = from[1] + ay;
      let wet = 0;
      while (map.inBounds(cx, cy) && wet <= MAX_BRIDGE_SPAN) {
        const t = map.terrain[map.idx(cx, cy)];
        run.push([cx, cy]);
        if (!isWater(t)) break;
        wet++;
        cx += ax;
        cy += ay;
      }
      // Only a crossing counts: it has to start in water and end on dry land.
      if (wet === 0 || wet > MAX_BRIDGE_SPAN) continue;
      const last = run[run.length - 1];
      if (!map.inBounds(last[0], last[1]) || isWater(map.terrain[map.idx(last[0], last[1])])) continue;
      if (wet < bestSpan) {
        bestSpan = wet;
        deck = run;
      }
    }
    if (deck.length > 0) {
      for (const [wx, wy] of deck) {
        if (!map.inBounds(wx, wy)) continue;
        // Water under the line becomes deck; the dry end becomes road, so the
        // crossing is joined to the track rather than being a span with a gap
        // in front of it.
        if (isWater(map.terrain[map.idx(wx, wy)])) map.terrain[map.idx(wx, wy)] = Terrain.Bridge;
        else pave(map, wx, wy);
      }
      /**
       * Walk the road from the far end of the deck back to where the wander
       * came ashore, so the track actually reaches the bridge it just built.
       * Without this the deck lands square across the channel and the road
       * arrives forty tiles down the bank from it.
       */
      const land = deck[deck.length - 1];
      const rejoin = j < line.length ? line[j] : line[line.length - 1];
      for (const step of walkLine(land[0], land[1], rejoin[0], rejoin[1])) {
        if (map.inBounds(step[0], step[1]) && !isWater(map.terrain[map.idx(step[0], step[1])])) {
          pave(map, step[0], step[1]);
        }
      }
    }
    i = j - 1;
  }
}

function carveRoads(map: TileMap, rng: Rng): void {
  /**
   * One or two roads, and they run *through* the town rather than at it.
   *
   * ── Why the spokes had to go ───────────────────────────────────────────────
   *
   * The last pass fixed the shape of each road and left the shape of the
   * *network* alone, which is where the look actually came from: one road per
   * gate, eight gates, all ending at the same tile. Making each spoke wander
   * turned a compass rose into a spider — the owner's word, and the right one.
   * No amount of per-road naturalism fixes eight roads meeting at a point,
   * because the point is the problem.
   *
   * ── What a road is for ─────────────────────────────────────────────────────
   *
   * A road is not a thing a settlement emits in every direction. It is a route
   * between two other places that a settlement grew up *on*, which is why
   * towns sit at crossings and fords. So a track now enters at one gate and
   * leaves by roughly the opposite one, passing through the town on its way —
   * and where two of them exist they cross near the Hearth, which is the oldest
   * reason for a town to be anywhere.
   *
   * The gates are untouched. All eight still exist and the director still picks
   * approach vectors from them (`planNextWave`); they simply are not all paved
   * any more. An unroaded gate is a horde coming cross-country, which is what
   * seven of the eight were always doing anyway.
   *
   * ── Two things that still read as geometry, and the fix for each ───────────
   *
   * The first cut of this ran gate `a` to gate `a + 4`. Those are *exactly*
   * opposite by construction, so the route was a dead-straight line through the
   * middle of the map, and at map scale a ±20-tile wander over 380 tiles is a
   * 4% deviation the eye reads as ruler-drawn. Pairing three, four or five
   * gates apart puts the run off-axis, and the wander is widened to match the
   * length it has to disguise.
   *
   * The second is the waypoint. Aiming both halves at the Hearth's own tile
   * makes every road *point at the player*, which is the spider again in
   * miniature. The track is aimed a short way off the Hearth instead, so the
   * settlement sits **beside** its road the way a real one does — close enough
   * that `touchesRoad`'s district bonus is still there for the taking, far
   * enough that the road plainly existed first.
   */
  const wanted = 1 + nextInt(rng, 0, 1);
  const first = nextInt(rng, 0, map.gates.length - 1);
  const used = new Set<number>();
  for (let n = 0; n < wanted; n++) {
    // Quarter-turn apart for the second road, so two tracks cross rather than
    // running alongside each other.
    const a = (first + n * 2) % map.gates.length;
    // Three to five gates on: roughly opposite, never exactly.
    const b = (a + 3 + nextInt(rng, 0, 2)) % map.gates.length;
    if (used.has(a) || used.has(b)) continue;
    used.add(a);
    used.add(b);
    const ga = map.gates[a];
    const gb = map.gates[b];
    // The town is beside the road, not at the end of it.
    const wx = map.hearthX + nextInt(rng, -7, 7);
    const wy = map.hearthY + nextInt(rng, -7, 7);
    carveTrack(map, rng, ga.x, ga.y, wx, wy);
    carveTrack(map, rng, wx, wy, gb.x, gb.y);
  }
}

/**
 * Bresenham, local to this file.
 *
 * `apply.ts` exports a `lineTiles` that does the same thing, and importing it
 * here would close a cycle — `apply.ts` imports `map.ts`. Generation runs once
 * per world and a dozen lines of walk is not worth restructuring two modules
 * over.
 */
function* walkLine(x0: number, y0: number, x1: number, y1: number): Generator<[number, number]> {
  let x = x0;
  let y = y0;
  const dx = Math.abs(x1 - x0);
  const dy = -Math.abs(y1 - y0);
  const sx = x0 < x1 ? 1 : -1;
  const sy = y0 < y1 ? 1 : -1;
  let err = dx + dy;
  let guard = 0;
  for (;;) {
    yield [x, y];
    if ((x === x1 && y === y1) || guard++ > 4096) return;
    const e2 = 2 * err;
    if (e2 >= dy) {
      err += dy;
      x += sx;
    }
    if (e2 <= dx) {
      err += dx;
      y += sy;
    }
  }
}

/**
 * Lay one tile of road, if the ground will take it.
 *
 * A road goes round water and round a leyline. The leyline rule is old and the
 * reason is written at `scatterSeams`: there are three patches on a map and
 * paving one could cost a run its only route to the second Age. The water rule
 * is what makes a river worth anything — a road that paved its way across a
 * channel would hand every horde a free bridge on turn zero.
 */
function pave(map: TileMap, x: number, y: number): void {
  if (!map.inBounds(x, y)) return;
  const i = map.idx(x, y);
  const t = map.terrain[i];
  if (t === Terrain.River || t === Terrain.Deep || t === Terrain.Leyline) return;
  map.terrain[i] = Terrain.Road;
}
